<template>
    <div ref="containerPaletteRef" class="c-palette-picker">
        <div class="c-palette-picker__header">
            <h3>Alternation</h3>
            <c-dropdown
                class="w-50"
                :options="paletteTypesList"
                :value="paletteType"
                :name="'paletteType'"
                placeholder="Select Palette Type"
                @input="onPaletteTypeChange"
            />
        </div>
        <div class="c-palette-picker__colors">
            <div class="c-palette-picker__colors-header">
                <h3>Colors</h3>
                <c-button size="icon" icon="plus" @click="() => addColorStop()" />
            </div>
            <div class="c-palette-picker__colors-list">
                <draggable-list v-model="paletteColorListSorted" item-key="index" hide-counter draggable selected="-1">
                    <template #item="{ element }">
                        <div class="c-palette-picker__colors-color">
                            <div
                                class="c-palette-picker__colors-preview"
                                :style="{ backgroundColor: element.hex }"
                                @click.stop="() => openColorPickerPopper(element.index)"
                            />
                            <span>{{ element.hex.replace('#', '').toUpperCase() }}</span>
                        </div>
                        <c-button
                            :disabled="!canDeleteColor"
                            size="icon"
                            icon="trash-can"
                            @click="() => onDeleteColor(element.index)"
                        />
                    </template>
                </draggable-list>
            </div>
        </div>
        <div ref="solidColorRef" class="color-selector-wrapper">
            <c-solid-color
                v-if="openColorPicker"
                v-model="currentColor"
                class="color-picker"
                :mode="mode"
                @input="onUpdateColor"
            >
                <template #footer>
                    <div class="d-flex justify-content-end mt-auto">
                        <c-button
                            class="mr-2"
                            variant="outline-primary"
                            size="sm"
                            text="CANCEL"
                            @click="onClosePopper"
                        />
                        <c-button variant="primary" size="sm" text="APPLY" @click="onClosePopper" />
                    </div>
                </template>
            </c-solid-color>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Ref, ref, computed, onMounted, onBeforeMount } from 'vue';
import CDropdown from '@/components/CDropdown.vue';
import CButton from '../CButton/CButton.vue';
import CSolidColor from './CSolidColor.vue';
import Popper from 'popper.js';
import { type FullColorObject, type PaletteColorObject, type FullPaletteObject, PaletteType, useColor } from '@/composables/color';
import { debounce } from 'lodash';
import DraggableList from '@/components/DesignStudio/DraggableList.vue';

interface PaletteColorSorted extends PaletteColorObject {
    index: number;
}

const props = withDefaults(
    defineProps<{
        value?: FullPaletteObject;
        mode?: 'cmyk' | 'rgb';
    }>(),
    {
        mode: 'rgb'
    }
);

const emit = defineEmits<{
    (event: 'input', value: FullPaletteObject): void;
}>();

const paletteTypesList = [
    {
        value: PaletteType.LETTER,
        text: 'One color per letter'
    }
];

const paletteColorList = ref<PaletteColorObject[]>([]);
const paletteType = ref<string>(PaletteType.LETTER);
const dragStopIndex = ref<number | null>(null);
const selectedStopIndex = ref<number>(-1);
const containerPaletteRef: Ref<HTMLDivElement | null> = ref(null);
const solidColorRef: Ref<HTMLDivElement | null> = ref(null);
const currentColor = ref<FullColorObject | undefined>();
const instancePopper = ref<Popper | null>(null);
const openColorPicker = ref(false);
const { getColorObj } = useColor();

const paletteColorListSorted = computed<PaletteColorSorted[]>({
    get: () => (paletteColorList.value.map((color, index) => ({
            ...color,
            index
        }))),
    set: (values: PaletteColorSorted[]) => {
        paletteColorList.value = values?.map((value: PaletteColorSorted) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { index, ...rest } = value;
            return rest;
        }) || [];
        onInputChange();
    }
});

const canDeleteColor = computed(() => {
    return paletteColorList.value.length > 1;
});

const addColorStop = () => {
    paletteColorList.value.push({ hex: '#ffffff', cmyk: { c: 0, m: 0, y: 0, k: 0}, alpha: 1 });
    onInputChange();
};

const onDeleteColor = (index: number) => {
    if (index < 0 || index >= paletteColorList.value.length) return;
    if (paletteColorList.value.length <= 1) {
        console.warn('Cannot delete color, at least two colors are required.');
        return;
    }
    paletteColorList.value.splice(index, 1);
    dragStopIndex.value = null;
    selectedStopIndex.value = -1;
    onInputChange();
};

const onPaletteTypeChange = (value: PaletteType) => {
    paletteType.value = value;
    onInputChange();
};

const setActiveStop = (index: number) => {
    selectedStopIndex.value = index;
    const colorSelected = paletteColorList.value[index]
        ? paletteColorList.value[index]
        : undefined;

    currentColor.value = getColorObj({ hex: colorSelected?.hex, a: colorSelected?.alpha || 1 });
};

const onUpdateColor = (color: FullColorObject) => {
    if (selectedStopIndex.value !== null && paletteColorList.value[selectedStopIndex.value]) {
        paletteColorList.value[selectedStopIndex.value].hex = color.hex;
    }
    onInputChange();
};

const openColorPickerPopper = (index: number) => {
    if (instancePopper.value) {
        instancePopper.value.destroy();
        instancePopper.value = null;
    }
    setActiveStop(index);
    if (!containerPaletteRef.value) {
        console.warn('Container reference is not set.');
        return;
    }
    let container: HTMLDivElement = containerPaletteRef.value;
    if (containerPaletteRef.value?.parentElement) {
        container = containerPaletteRef.value.parentElement as HTMLDivElement;
    }
    instancePopper.value = new Popper(container, solidColorRef.value as HTMLDivElement, {
        placement: 'top-end',
        modifiers: {
            flip: {
                enabled: false,
                behavior: ['top', 'bottom'],
                padding: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0
                }
            },
            preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport', // prevent overflow on window
                padding: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0 // prevent overflow on right side
                }
            },
            hide: {
                enabled: false
            },
            offset: {
                offset: '100px, -25%'
            }
        }
    });
    openColorPicker.value = true;
};

const onClosePopper = () => {
    if (instancePopper.value) {
        instancePopper.value.destroy();
        instancePopper.value = null;
    }
    openColorPicker.value = false;
};

const debounceUpdate = debounce(() => {
    const palette: FullPaletteObject = {
        colors: paletteColorList.value,
        type: paletteType.value as PaletteType
    };
    emit('input', palette);
}, 150);

const onInputChange = () => {
    debounceUpdate();
};

onMounted(() => {
    if (props.value) {
        paletteColorList.value = props.value.colors || [];
        paletteType.value = props.value.type || PaletteType.LETTER;
    } else {
        const color1 = getColorObj({ hex: '#23E982', a: 1 });
        const color2 = getColorObj({ hex: '#000000', a: 1 });
        paletteColorList.value = [
            {
                hex: color1?.hex || '#23E982',
                cmyk: color1?.cmyk || { c: 0, m: 0, y: 0, k: 0 },
                alpha: color1?.a || 1
            } as PaletteColorObject,
            {
                hex: color2?.hex || '#000000',
                cmyk: color2?.cmyk || { c: 0, m: 0, y: 0, k: 0 },
                alpha: color2?.a || 1
            } as PaletteColorObject
        ];
        paletteType.value = PaletteType.LETTER;
    }
    onInputChange();
});

onBeforeMount(() => {
    onInputChange();
});
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.color-selector-wrapper {
    position: relative;
    width: 28rem;
    z-index: 9999;
    .color-picker {
        position: absolute;
        width: 100%;
        background-color: get-color('white');
        padding: 0.625rem;
        border-radius: 0.3125rem;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15), 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    }
}

.c-palette-picker {
    width: 100%;
    margin-bottom: 0.625rem;
    h3 {
        margin: 0;
        font-size: 0.875rem;
        font-weight: bold;
        color: get-color('gray-600');
    }
    &__header {
        display: flex;
        flex-direction: column;
        margin-bottom: 0.625rem;
        h3 {
            margin-bottom: 0.19rem;
        }
    }
    &__colors {
        &-header {
            margin-bottom: 0.625rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        &-list {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            overflow: hidden;
            max-height: 200px;
        }
        &-color {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            width: 100%;
            padding: 0.3125rem;
            border: 1px solid get-color('gray-light');
            border-radius: 0.125rem;
            background-color: get-color('white');
        }
        &-preview {
            width: 1.25rem;
            height: 1.0625rem;
            border-radius: 0.125rem;
            border: 1px solid get-color('gray-light');
            cursor: pointer;
        }
        &-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem;
            align-items: center;
            justify-content: space-between;
            &.active {
                background-color: get-color('purple-lightest');
            }
        }
    }
}
</style>
